<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { Mic, RotateCcw, Square, TriangleAlert } from 'lucide-vue-next';

const cl_props = withDefaults(defineProps<{ cl_maxSeconds?: number }>(), { cl_maxSeconds: 5 });
const cl_emit = defineEmits<{ cl_ready: [cl_blob: Blob]; cl_error: [cl_message: string] }>();

const cl_state = ref<'idle' | 'recording' | 'ready'>('idle');
const cl_seconds = ref(0);
const cl_level = ref(0);
const cl_audioUrl = ref('');
const cl_error = ref('');
let cl_recorder: MediaRecorder | null = null;
let cl_stream: MediaStream | null = null;
let cl_chunks: Blob[] = [];
let cl_timer: number | undefined;
let cl_startedAt = 0;
let cl_audioContext: AudioContext | null = null;
let cl_animationFrame = 0;

const cl_timeLabel = computed(() => `${cl_seconds.value.toFixed(1)}s / ${cl_props.cl_maxSeconds}s`);

function cl_supportedMime() {
  const cl_candidates = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'];
  return cl_candidates.find((cl_type) => MediaRecorder.isTypeSupported(cl_type)) ?? '';
}

async function cl_start() {
  cl_error.value = '';
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    cl_fail('当前浏览器不支持录音，请使用最新版 Safari、Chrome 或 Edge。'); return;
  }
  try {
    cl_stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
    const cl_mimeType = cl_supportedMime();
    cl_recorder = new MediaRecorder(cl_stream, cl_mimeType ? { mimeType: cl_mimeType } : undefined);
    cl_chunks = [];
    cl_recorder.ondataavailable = (cl_event) => { if (cl_event.data.size) cl_chunks.push(cl_event.data); };
    cl_recorder.onstop = cl_finish;
    cl_recorder.start(200);
    cl_state.value = 'recording'; cl_startedAt = performance.now();
    cl_setupMeter(cl_stream);
    cl_timer = window.setInterval(() => {
      cl_seconds.value = (performance.now() - cl_startedAt) / 1000;
      if (cl_seconds.value >= cl_props.cl_maxSeconds) cl_stop();
    }, 100);
  } catch (cl_cause) {
    const cl_name = cl_cause instanceof DOMException ? cl_cause.name : '';
    cl_fail(cl_name === 'NotAllowedError' ? '麦克风权限被拒绝。请在浏览器设置中允许麦克风后重试。' : cl_name === 'NotFoundError' ? '没有检测到可用麦克风。' : '无法启动麦克风，请检查设备和浏览器权限。');
  }
}

function cl_stop() {
  if (cl_recorder?.state === 'recording') cl_recorder.stop();
}

function cl_finish() {
  cl_cleanupMedia();
  const cl_blob = new Blob(cl_chunks, { type: cl_recorder?.mimeType || cl_chunks[0]?.type || 'audio/webm' });
  if (cl_blob.size < 500) { cl_fail('没有录到声音，请重新录制。'); return; }
  if (cl_audioUrl.value) URL.revokeObjectURL(cl_audioUrl.value);
  cl_audioUrl.value = URL.createObjectURL(cl_blob);
  cl_state.value = 'ready'; cl_emit('cl_ready', cl_blob);
}

function cl_reset() {
  cl_cleanupMedia();
  if (cl_audioUrl.value) URL.revokeObjectURL(cl_audioUrl.value);
  cl_audioUrl.value = ''; cl_seconds.value = 0; cl_level.value = 0; cl_state.value = 'idle'; cl_chunks = [];
}

function cl_setupMeter(cl_media: MediaStream) {
  cl_audioContext = new AudioContext();
  const cl_source = cl_audioContext.createMediaStreamSource(cl_media);
  const cl_analyser = cl_audioContext.createAnalyser();
  cl_analyser.fftSize = 256; cl_source.connect(cl_analyser);
  const cl_data = new Uint8Array(cl_analyser.frequencyBinCount);
  function cl_draw() {
    cl_analyser.getByteFrequencyData(cl_data);
    cl_level.value = Math.min(100, cl_data.reduce((cl_sum, cl_item) => cl_sum + cl_item, 0) / cl_data.length * 1.7);
    cl_animationFrame = requestAnimationFrame(cl_draw);
  }
  cl_draw();
}

function cl_cleanupMedia() {
  if (cl_timer) clearInterval(cl_timer);
  cancelAnimationFrame(cl_animationFrame);
  cl_stream?.getTracks().forEach((cl_track) => cl_track.stop());
  void cl_audioContext?.close();
  cl_timer = undefined; cl_stream = null; cl_audioContext = null; cl_level.value = 0;
}

function cl_fail(cl_message: string) { cl_cleanupMedia(); cl_state.value = 'idle'; cl_error.value = cl_message; cl_emit('cl_error', cl_message); }
onBeforeUnmount(() => { cl_cleanupMedia(); if (cl_audioUrl.value) URL.revokeObjectURL(cl_audioUrl.value); });
</script>

<template>
  <div class="cl-recorder">
    <div v-if="cl_state === 'recording'" class="cl-wave" aria-label="正在录音">
      <span v-for="cl_bar in 12" :key="cl_bar" :style="{ height: `${Math.max(10, cl_level * ((cl_bar % 4 + 2) / 5))}%` }" />
    </div>
    <p v-if="cl_state === 'recording'" class="cl-recording-label"><span /> Recording... {{ cl_timeLabel }}</p>
    <button v-if="cl_state === 'idle'" class="cl-record-button" type="button" title="开始录音" @click="cl_start"><Mic :size="34" /><span>Read it</span></button>
    <button v-else-if="cl_state === 'recording'" class="cl-record-button cl-record-button--stop" type="button" title="停止录音" @click="cl_stop"><Square :size="30" /><span>Stop</span></button>
    <div v-else class="cl-recorder-ready">
      <audio :src="cl_audioUrl" controls playsinline />
      <button class="cl-icon-button" type="button" title="重新录制" @click="cl_reset"><RotateCcw /><span>重录</span></button>
    </div>
    <p v-if="cl_error" class="cl-inline-error"><TriangleAlert :size="18" />{{ cl_error }}</p>
  </div>
</template>
